import { useEffect } from 'react';

import { useFirestoreDocument } from '@/hooks/useFirestoreDocument';
import { collections } from '@/lib/firebase';
import {
  parsePermissions,
  signOut,
  updateMemberships,
  useSession,
  type Membership,
} from './session';

function toMembership(organizationId: string, fields: Record<string, unknown>): Membership | null {
  if (fields.is_active !== true) return null;
  return {
    organizationId,
    role: fields.role === 'admin' ? 'admin' : 'member',
    permissions: parsePermissions(fields.permissions),
  };
}

/**
 * Reads the active memberships out of one user document.
 *
 * A revocation stays in the `organizations` map with `is_active` false, so
 * anything but true is dropped — the same list the session endpoint reports.
 * Sorted by organization id, because that is the order it reports them in and a
 * differently ordered but equal list would read as a change.
 */
export function parseMemberships(raw: Record<string, unknown>): Membership[] {
  const stored = raw.organizations;
  if (typeof stored !== 'object' || stored === null) return [];
  return Object.entries(stored as Record<string, unknown>)
    .filter(([, fields]) => typeof fields === 'object' && fields !== null)
    .map(([organizationId, fields]) =>
      toMembership(organizationId, fields as Record<string, unknown>),
    )
    .filter((membership): membership is Membership => membership !== null)
    .sort((left, right) => left.organizationId.localeCompare(right.organizationId));
}

/**
 * Keeps the signed-in user's own role and permissions live.
 *
 * The session is written once at sign-in, so without this an admin who demotes
 * somebody changes nothing until that person signs in again. Losing the active
 * membership ends the session instead of narrowing it: the rules stop answering
 * for that organization, so there is nothing left to render.
 */
export function useLiveMembership(): void {
  const session = useSession();
  const email = session?.email ?? null;
  const organizationId = session?.organizationId ?? null;

  const { data } = useFirestoreDocument<Membership[]>({
    path: collections.user(email ?? '_'),
    parse: parseMemberships,
    enabled: Boolean(email),
  });

  useEffect(() => {
    if (!data || !organizationId) return;
    if (!data.some((membership) => membership.organizationId === organizationId)) {
      signOut('revoked');
      return;
    }
    updateMemberships(data);
  }, [data, organizationId]);
}
